'use client'

/**
 * Password Reset Request Page
 * Allows users to request a password reset email
 */

import { useState } from 'react'
import Link from 'next/link'
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react'
import { Button } from '@/components/atoms/Button'
import { Card } from '@/components/atoms/Card'
import { Text, Heading } from '@/components/atoms/Text'
import Image from 'next/image'

export default function ResetPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to send reset email')
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--hg-bg-primary, #0a0a0a)' }}>
        <Card padding="lg" className="max-w-md w-full">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--hg-cyan-bg)]">
              <CheckCircle className="w-6 h-6 text-[var(--hg-cyan-text)]" />
            </div>
            <Heading as="h2" size="2xl">Check your email</Heading>
            <Text variant="muted">
              We sent a password reset link to <strong className="hg-text-primary">{email}</strong>.
              Check your inbox and follow the instructions.
            </Text>
            <Text variant="muted" size="sm">The link will expire in 1 hour.</Text>
            <Link href="/login" className="mt-4">
              <Button variant="secondary" icon={<ArrowLeft className="w-4 h-4" />}>
                Back to login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--hg-bg-primary, #0a0a0a)' }}>
      <Card padding="lg" className="max-w-md w-full">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl overflow-hidden mb-4">
            <Image src="/hmn_logo.png" alt="HMN" width={48} height={48} className="w-full h-full object-contain" />
          </div>
          <Heading as="h2" size="2xl" className="text-center">Reset your password</Heading>
          <Text variant="muted" size="sm" className="mt-2 text-center">
            Enter your email and we&apos;ll send you a link to reset your password.
          </Text>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
              <Text size="sm" className="text-red-400">{error}</Text>
            </div>
          )}

          <div>
            <label htmlFor="email-address" className="block text-sm font-medium hg-text-secondary mb-1.5 font-diatype">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 hg-text-muted" />
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border transition-all font-diatype text-sm hg-bg-secondary hg-border hg-text-primary placeholder:text-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-border)] focus:border-[var(--hg-cyan-border)]"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={loading}
            icon={<Mail className="w-4 h-4" />}
          >
            Send reset link
          </Button>

          <div className="text-center">
            <Link href="/login" className="text-sm text-[var(--hg-cyan-text)] hover:underline font-diatype">
              Back to login
            </Link>
          </div>
        </form>
      </Card>
    </div>
  )
}
